<template>
  <div class="app-wrapper" :class="{ 'is-mobile': isMobile }">
    <!-- 移动端遮罩 -->
    <div v-if="isMobile && mobileSidebarOpen" class="mobile-overlay" @click="mobileSidebarOpen = false"></div>
    <div class="sidebar-container" :class="{ 'mobile-open': mobileSidebarOpen }" :style="isMobile ? {} : { width: sidebarWidth + 'px' }">
      <div class="sidebar-scroll">
        <el-menu
          :default-active="activeMenu"
          :collapse="false"
          :background-color="variables.menuBg"
          :text-color="variables.menuText"
          :active-text-color="variables.menuActiveText"
          :unique-opened="false"
          :collapse-transition="false"
          mode="vertical"
        >
          <sidebar-item
            v-for="route in routes"
            :key="route.path"
            :item="route"
            :base-path="route.path"
          />
        </el-menu>
      </div>
      <div class="sidebar-resize-handle" @mousedown.prevent="startResize" />
    </div>
    <div class="main-container" :style="{ marginLeft: sidebarWidth + 'px' }">
      <div class="navbar">
        <div v-if="isMobile" class="hamburger-btn" @click="mobileSidebarOpen = !mobileSidebarOpen">
          <i class="el-icon-s-unfold"></i>
        </div>
        <div class="breadcrumb">
          <el-breadcrumb separator="/">
            <el-breadcrumb-item
              v-for="(item, index) in levelList"
              :key="item.path"
            >
              <span
                v-if="item.redirect === 'noRedirect' || index === levelList.length - 1"
                class="no-redirect"
              >{{ item.meta.title }}</span>
              <a v-else @click.prevent="handleLink(item)">{{ item.meta.title }}</a>
            </el-breadcrumb-item>
          </el-breadcrumb>
        </div>
        <div class="right-menu">
          <el-dropdown class="avatar-container" trigger="click">
            <div class="avatar-wrapper">
              <i class="el-icon-user-solid" style="margin-right: 8px; font-size: 18px; color: #409EFF;"></i>
              <span class="user-name">{{ name || '未登录' }}</span>
              <i class="el-icon-caret-bottom" />
            </div>
            <el-dropdown-menu slot="dropdown" class="user-dropdown">
              <el-dropdown-item @click.native="openChangePasswordDialog">
                <i class="el-icon-key" style="margin-right: 8px;" />
                <span>修改密码</span>
              </el-dropdown-item>
              <el-dropdown-item divided @click.native="logout">
                <i class="el-icon-switch-button" style="margin-right: 8px;"></i>
                <span>退出登录</span>
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </div>
      <div class="app-main">
        <router-view />
      </div>
    </div>

    <el-dialog
      title="修改密码"
      :visible.sync="passwordDialogVisible"
      width="500px"
      append-to-body
      class="change-password-dialog"
      @closed="resetPasswordForm"
    >
      <el-form
        :key="'pwd-form-' + passwordFormKey"
        ref="passwordFormRef"
        :rules="passwordFormRules"
        :model="passwordForm"
        label-position="left"
        label-width="100px"
        class="change-password-dialog__form"
        autocomplete="off"
      >
        <div class="pwd-rule-card pwd-rule-card--compact">
          <div class="pwd-rule-card__head">
            <i class="el-icon-lock pwd-rule-card__icon" />
            <span class="pwd-rule-card__title">密码规则要求</span>
          </div>
          <ol class="pwd-rule-card__list">
            <li>长度至少 8 位</li>
            <li>须包含大写字母、小写字母、数字、特殊字符中的至少三种</li>
            <li>禁止使用常见弱口令（如 "123456"、"password" 等）</li>
            <li>不能与用户名相同（不区分大小写）</li>
          </ol>
        </div>
        <el-form-item label="用户名">
          <el-input v-model="passwordForm.username" disabled />
        </el-form-item>
        <el-form-item label="原密码" prop="oldPassword">
          <el-input
            v-model="passwordForm.oldPassword"
            type="password"
            placeholder="请手动输入当前登录密码"
            name="oa-verify-current-password"
            autocomplete="off"
            :readonly="oldPasswordReadonly"
            show-password
            @mousedown.native="onOldPasswordFocus"
            @focus="onOldPasswordFocus"
          />
        </el-form-item>
        <el-form-item label="新密码" prop="newPassword">
          <el-input
            v-model="passwordForm.newPassword"
            type="password"
            placeholder="请输入新密码"
            autocomplete="new-password"
            show-password
          />
        </el-form-item>
        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input
            v-model="passwordForm.confirmPassword"
            type="password"
            placeholder="请再次输入新密码"
            autocomplete="new-password"
            show-password
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="passwordDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="passwordSaving" @click="submitChangePassword">
          确认
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import path from 'path-browserify'
import { mapGetters } from 'vuex'
import SidebarItem from './SidebarItem'
import variables from '../styles/variables.scss'
import { updateUserPassword } from '../api/user'
import { getPasswordPolicyError } from '../utils/passwordPolicy'

/** 从路由树中收集审批分类页的 flowCode（path 为 /approval/<分组>/xxx 的末段） */
function collectApprovalFlowCodes(routes, basePath = '') {
  const list = []
  if (!Array.isArray(routes)) return list
  routes.forEach(route => {
    const full = path.resolve(basePath || '/', route.path || '')
    const normalized = full.replace(/\/+/g, '/')
    const m = normalized.match(/^\/approval\/[^/]+\/([^/]+)$/)
    if (m) list.push(m[1])
    if (route.children && route.children.length) {
      list.push(...collectApprovalFlowCodes(route.children, normalized))
    }
  })
  return list
}

const SIDEBAR_WIDTH_MIN = 160
const SIDEBAR_WIDTH_MAX = 400
const SIDEBAR_WIDTH_DEFAULT = 200

export default {
  name: 'Layout',
  components: {
    SidebarItem
  },
  data() {
    return {
      sidebarWidth: SIDEBAR_WIDTH_DEFAULT,
      isMobile: false,
      mobileSidebarOpen: false,
      passwordDialogVisible: false,
      passwordSaving: false,
      /** 每次打开弹窗递增，避免浏览器在上一次实例上保留自动填充状态 */
      passwordFormKey: 0,
      /** 先只读，聚焦后再可输入，减轻浏览器对「当前密码」类字段的自动填充 */
      oldPasswordReadonly: true,
      passwordForm: {
        id: null,
        username: '',
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      }
    }
  },
  watch: {
    '$route'() {
      if (this.isMobile) this.mobileSidebarOpen = false
    },
    'passwordForm.newPassword'() {
      if (this.passwordForm.confirmPassword && this.$refs.passwordFormRef) {
        this.$nextTick(() => {
          this.$refs.passwordFormRef.validateField('confirmPassword')
        })
      }
    },
    'passwordForm.oldPassword'() {
      if (this.passwordForm.newPassword && this.$refs.passwordFormRef) {
        this.$nextTick(() => {
          this.$refs.passwordFormRef.validateField('newPassword')
        })
      }
    }
  },
  mounted() {
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
    this.fetchApprovalTodoCounts()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile)
  },
  computed: {
    ...mapGetters('user', [
      'name',
      'userId',
      'username'
    ]),
    routes() {
      return this.$store.state.permission.routes || []
    },
    activeMenu() {
      const route = this.$route
      const { meta, path } = route
      // 如果设置路径，侧边栏将突出显示您设置的路径
      if (meta.activeMenu) {
        return meta.activeMenu
      }
      return path
    },
    variables() {
      return variables
    },
    levelList() {
      return this.$route.matched.filter(item => item.meta && item.meta.title)
    },
    passwordFormRules() {
      return {
        oldPassword: [
          { required: true, message: '请输入原密码', trigger: 'blur' }
        ],
        newPassword: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          {
            validator: (rule, value, callback) => {
              const err = getPasswordPolicyError(value, this.passwordForm.username)
              if (err) {
                callback(new Error(err))
                return
              }
              if (value && value === this.passwordForm.oldPassword) {
                callback(new Error('新密码不能与当前密码相同'))
                return
              }
              callback()
            },
            trigger: ['blur', 'change']
          }
        ],
        confirmPassword: [
          { required: true, message: '请再次输入新密码', trigger: 'blur' },
          {
            validator: (rule, value, callback) => {
              if (value !== this.passwordForm.newPassword) {
                callback(new Error('两次输入的密码不一致'))
              } else {
                callback()
              }
            },
            trigger: ['blur', 'change']
          }
        ]
      }
    }
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth < 768
      if (!this.isMobile) this.mobileSidebarOpen = false
    },
    fetchApprovalTodoCounts() {
      const routes = this.$store.state.permission.routes || []
      const flowCodes = collectApprovalFlowCodes(routes)
      if (flowCodes.length) {
        this.$store.dispatch('approval/fetchTodoCounts', flowCodes)
      }
    },
    handleLink(item) {
      const { redirect, path: routePath } = item
      if (redirect) {
        this.$router.push(redirect)
        return
      }
      this.$router.push(routePath)
    },
    async logout() {
      await this.$store.dispatch('user/logout')
      this.$router.push(`/login?redirect=${this.$route.fullPath}`)
    },
    resetPasswordForm() {
      this.oldPasswordReadonly = true
      this.passwordForm = {
        id: null,
        username: '',
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      }
      this.passwordSaving = false
      if (this.$refs.passwordFormRef) {
        this.$refs.passwordFormRef.clearValidate()
      }
    },
    openChangePasswordDialog() {
      const id = this.userId
      const uname = this.username
      if (!id) {
        this.$message.warning('未获取到用户信息，请重新登录')
        return
      }
      this.passwordFormKey += 1
      this.oldPasswordReadonly = true
      this.passwordForm = {
        id,
        username: uname || '',
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      }
      this.passwordDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.passwordFormRef) {
          this.$refs.passwordFormRef.clearValidate()
        }
      })
    },
    submitChangePassword() {
      if (!this.$refs.passwordFormRef) return
      this.$refs.passwordFormRef.validate(valid => {
        if (!valid || this.passwordSaving) return
        this.passwordSaving = true
        updateUserPassword({
          id: this.passwordForm.id,
          oldPassword: this.passwordForm.oldPassword,
          password: this.passwordForm.newPassword
        })
          .then(async () => {
            this.passwordDialogVisible = false
            this.$message.success('密码修改成功，请使用新密码重新登录')
            await this.$store.dispatch('user/logout')
            this.$router.push('/login')
          })
          .finally(() => {
            this.passwordSaving = false
          })
      })
    },
    onOldPasswordFocus() {
      this.oldPasswordReadonly = false
    },
    startResize(e) {
      const startX = e.clientX
      const startW = this.sidebarWidth
      const onMove = (e2) => {
        const dx = e2.clientX - startX
        let w = startW + dx
        w = Math.max(SIDEBAR_WIDTH_MIN, Math.min(SIDEBAR_WIDTH_MAX, w))
        this.sidebarWidth = w
      }
      const onUp = () => {
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
    }
  }
}
</script>

<style lang="scss" scoped>
.app-wrapper {
  position: relative;
  height: 100%;
  width: 100%;

  &::after {
    content: '';
    display: table;
    clear: both;
  }
}

.sidebar-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
}

.sidebar-container {
  display: flex;
  flex-direction: column;
  position: relative;
  float: left;
  width: 200px;
  height: 100vh;
  background-color: #304156;
  overflow: hidden;

  .el-menu {
    border: none;
    width: 100% !important;
    min-height: min-content;
  }
  .sidebar-title-wrap {
    display: inline-flex;
    align-items: center;
  }
  .sidebar-menu-badge {
    margin-left: 5px;
    vertical-align: middle;
  }
  /* 深色背景下角标：小一点、与文字有间隔、略上移 */
  ::v-deep .sidebar-menu-badge .el-badge__content {
    background-color: #f56c6c;
    color: #fff;
    border: none;
    height: 18px;
    min-width: 18px;
    padding: 0 5px;
    box-sizing: border-box;
    line-height: 18px;
    font-size: 12px;
    font-weight: 600;
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    border-radius: 999px !important;
    transform: translateX(5px) translateY(-5px);
  }
  .sidebar-resize-handle {
    position: absolute;
    top: 0;
    right: 0;
    width: 6px;
    height: 100%;
    cursor: col-resize;
    z-index: 1;
  }
  .sidebar-resize-handle:hover {
    background: rgba(255, 255, 255, 0.08);
  }
}

.main-container {
  min-height: 100%;
  transition: margin-left 0.28s;
  margin-left: 200px;
  position: relative;
}

.navbar {
  height: 50px;
  overflow: hidden;
  position: relative;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;

  .breadcrumb {
    display: inline-block;
    font-size: 14px;
    line-height: 50px;
    margin-left: 8px;

    .no-redirect {
      color: #97a8be;
      cursor: text;
    }
  }

  .right-menu {
    float: right;
    height: 100%;
    line-height: 50px;

    &:focus {
      outline: none;
    }

    .right-menu-item {
      display: inline-block;
      padding: 0 8px;
      height: 100%;
      font-size: 18px;
      color: #5a5e66;
      vertical-align: text-bottom;

      &.hover-effect {
        cursor: pointer;
        transition: background 0.3s;

        &:hover {
          background: rgba(0, 0, 0, 0.025);
        }
      }
    }

    .avatar-container {
      .avatar-wrapper {
        margin-top: 0;
        position: relative;
        cursor: pointer;
        display: flex;
        align-items: center;
        padding: 5px 10px;
        border-radius: 4px;
        transition: background-color 0.3s;

        &:hover {
          background-color: rgba(0, 0, 0, 0.025);
        }

        .user-name {
          font-size: 14px;
          color: #606266;
          margin-right: 5px;
        }

        .user-avatar {
          cursor: pointer;
          width: 40px;
          height: 40px;
          border-radius: 10px;
        }

        .el-icon-caret-bottom {
          cursor: pointer;
          font-size: 12px;
          color: #909399;
          margin-left: 5px;
        }
      }
    }
  }
}

.app-main {
  height: calc(100vh - 50px);
  width: 100%;
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: #f0f2f5;
}

/* 与系统用户管理「修改密码」说明块一致 */
.pwd-rule-card {
  margin: 0 0 20px;
  padding: 12px 14px;
  background: #e8f2fc;
  border: 1px solid #cce6ff;
  border-radius: 8px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.75);
}

.pwd-rule-card--compact {
  margin-bottom: 16px;
}

.pwd-rule-card__head {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(64, 158, 255, 0.22);
}

.pwd-rule-card__icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  margin-right: 8px;
  font-size: 13px;
  line-height: 24px;
  text-align: center;
  color: #409eff;
  background: rgba(255, 255, 255, 0.75);
  border-radius: 6px;
}

.pwd-rule-card__title {
  font-size: 12px;
  font-weight: 600;
  color: #303133;
  letter-spacing: 0.02em;
}

.pwd-rule-card__list {
  margin: 0;
  padding-left: 1.25em;
  font-size: 12px;
  color: #545c6b;
  line-height: 1.6;
}

.pwd-rule-card__list li {
  margin-bottom: 3px;
  padding-left: 1px;
}

.pwd-rule-card__list li:last-child {
  margin-bottom: 0;
}

.mobile-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.3); z-index: 998; }
.hamburger-btn { display: flex; align-items: center; justify-content: center; width: 40px; height: 40px; font-size: 22px; cursor: pointer; color: #5a5e66; flex-shrink: 0; border-radius: 4px; }
.hamburger-btn:hover { background: rgba(0,0,0,0.05); }
@media (max-width: 768px) {
  .sidebar-container { position: fixed !important; top: 0; left: 0; z-index: 999; width: 260px !important; transform: translateX(-100%); transition: transform 0.3s ease; box-shadow: none; }
  .sidebar-container.mobile-open { transform: translateX(0); box-shadow: 2px 0 8px rgba(0,0,0,0.15); }
  .sidebar-resize-handle { display: none !important; }
  .main-container { margin-left: 0 !important; }
  .navbar {
    padding: 0 10px;
    justify-content: space-between;
    position: relative;
  }
  .navbar .hamburger-btn {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    font-size: 20px;
  }
  .navbar .breadcrumb {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    margin-left: 0;
    line-height: 50px;
    font-size: 15px;
    font-weight: 600;
    white-space: nowrap;
  }
  .navbar .right-menu {
    flex-shrink: 0;
    height: auto;
    line-height: normal;
    display: flex;
    align-items: center;
  }
  .navbar .right-menu .avatar-wrapper {
    margin-top: 0;
    padding: 4px 8px;
  }
  .navbar .avatar-wrapper .user-name { max-width: 80px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  ::v-deep .el-dialog { width: 95% !important; margin-top: 5vh !important; }
  .app-main { -webkit-overflow-scrolling: touch; }
}
</style>